import {
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '@/shared/ui/icon';
import { useAuth } from '@/features/identity-management/auth/hooks/useAuth';
import { useT, type MessageKey } from '@/shared/i18n';
import { filterAppNavItems } from './app-nav';
import { navIconName, navSection, type NavSection } from './nav-icons';

interface Destination {
  key: string;
  label: string;
  to: string;
  icon: IconName;
  section: NavSection;
}

const SECTION_ORDER: NavSection[] = ['workspace', 'employer', 'admin'];
const SECTION_LABEL: Record<NavSection, MessageKey> = {
  workspace: 'appShell.search.section.workspace',
  employer: 'appShell.search.section.employer',
  admin: 'appShell.search.section.admin',
};

/**
 * Top-bar quick-nav. When focused with no query, it shows a collapsible TREE of
 * every destination the current user can reach, grouped by section, each with
 * its own icon. Typing filters to a flat match list (keyboard ↑/↓ + Enter).
 * Click navigates. Fully client-side — there is no content-search backend yet.
 */
export function TopbarSearch(): ReactNode {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [collapsed, setCollapsed] = useState<Set<NavSection>>(new Set());
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const destinations = useMemo<Destination[]>(
    () =>
      filterAppNavItems(user)
        .filter((item) => Boolean(item.to))
        .map((item) => ({
          key: item.key,
          label: t(item.labelKey),
          to: item.to as string,
          icon: navIconName(item.key),
          section: navSection(item.key),
        })),
    [user, t],
  );

  const q = query.trim().toLowerCase();
  const isSearching = q.length > 0;

  const matches = useMemo<Destination[]>(() => {
    if (!isSearching) return [];
    return destinations.filter((d) => d.label.toLowerCase().includes(q)).slice(0, 8);
  }, [destinations, q, isSearching]);

  const grouped = useMemo(
    () =>
      SECTION_ORDER.map((section) => ({
        section,
        items: destinations.filter((d) => d.section === section),
      })).filter((g) => g.items.length > 0),
    [destinations],
  );

  function go(to: string): void {
    navigate(to);
    setQuery('');
    setOpen(false);
  }

  function toggleSection(section: NavSection): void {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!isSearching || !matches.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((a) => (a + 1) % matches.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((a) => (a - 1 + matches.length) % matches.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = matches[active] ?? matches[0];
      if (target) go(target.to);
    }
  }

  const rowClass = (highlighted: boolean): string =>
    `flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-start text-sm transition-colors ${
      highlighted
        ? 'bg-[var(--surface-overlay)] text-[var(--text-primary)]'
        : 'text-[var(--text-secondary)] hover:bg-[var(--surface-overlay)]'
    }`;

  return (
    <div className="relative w-full max-w-sm">
      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          const target = matches[active] ?? matches[0];
          if (target) go(target.to);
        }}
        className="relative"
      >
        <Icon
          name="search"
          size={16}
          className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
        />
        <input
          type="search"
          autoComplete="off"
          aria-label={t('appShell.topbar.search.label')}
          placeholder={t('appShell.topbar.search.placeholder')}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 140);
          }}
          onKeyDown={onKeyDown}
          className="h-10 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] ps-9 pe-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] transition-colors hover:border-[var(--border-strong)] focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/40"
        />
      </form>

      {open ? (
        <div
          // Above page content (z-[80]) but below the modal layer (z-[100]).
          className="absolute start-0 top-[calc(100%+0.5rem)] z-[80] max-h-[70vh] w-72 overflow-y-auto rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-1 shadow-panel"
          // Keep the list open while clicking inside it.
          onMouseDown={(event) => event.preventDefault()}
        >
          {isSearching ? (
            matches.length === 0 ? (
              <p className="px-3 py-2 text-sm text-[var(--text-muted)]">
                {t('appShell.topbar.search.empty')}
              </p>
            ) : (
              <ul role="listbox">
                {matches.map((result, index) => (
                  <li key={result.key} role="option" aria-selected={index === active}>
                    <button
                      type="button"
                      onMouseEnter={() => setActive(index)}
                      onClick={() => go(result.to)}
                      className={rowClass(index === active)}
                    >
                      <Icon name={result.icon} size={16} className="text-[var(--text-muted)]" />
                      {result.label}
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="flex flex-col gap-1">
              {grouped.map(({ section, items }) => {
                const isCollapsed = collapsed.has(section);
                return (
                  <div key={section}>
                    <button
                      type="button"
                      onClick={() => toggleSection(section)}
                      aria-expanded={!isCollapsed}
                      className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-overlay)]"
                    >
                      <Icon
                        name={isCollapsed ? 'chevron-right' : 'chevron-down'}
                        size={12}
                      />
                      {t(SECTION_LABEL[section])}
                    </button>
                    {isCollapsed ? null : (
                      <ul>
                        {items.map((item) => (
                          <li key={item.key}>
                            <button
                              type="button"
                              onClick={() => go(item.to)}
                              className={rowClass(false)}
                            >
                              <Icon
                                name={item.icon}
                                size={16}
                                className="text-[var(--text-muted)]"
                              />
                              {item.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
